/**
 * Entity-workspace sidebar — the takeover counterpart of AdminSidebar.
 *
 * When an org or user page takes over the chrome, this renders in the global
 * sidebar's exact slot with its exact shell (width, list styling) so the
 * takeover swaps the sidebar's content, not its look. The header is a
 * context stack showing the full global → org → user ancestry: each
 * ancestor row is a one-click jump to that level, and the last row is the
 * current entity — bold, with a chip naming its level — so it's always
 * visible which level you're on.
 */

import { Fragment, useState } from 'react';
import { KeyboardArrowDown, OpenInNew, SubdirectoryArrowRight } from '@mui/icons-material';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import type { NavGroup } from './types';

export interface ContextSwitcherOption {
  id: string;
  label: string;
  /** Second line in the menu, e.g. the user's email. */
  secondary?: string;
}

export interface ContextStackEntry {
  /** Display name: "Admin v2", the org's name, or the user's name. */
  label: string;
  /** Level chip shown on the current (last) entry, e.g. "Org" or "User". */
  chip?: string;
  /** Navigate to this level (for the current entry: back to its overview). */
  onSelect: () => void;
  /**
   * When set, the entry grows a dropdown arrow that opens a menu of sibling
   * entities at this level (e.g. the org's other users), so switching entity
   * doesn't require a round-trip through the parent page.
   */
  switcher?: {
    options: ContextSwitcherOption[];
    /** The option matching the current entity — highlighted, selecting it is a no-op. */
    activeId?: string;
    onSwitch: (id: string) => void;
    /**
     * Escape hatch rendered under a divider at the menu's end — for callers
     * that cap `options`, e.g. "View all 56 members" jumping to a searchable
     * list. Omit when options are complete.
     */
    footer?: { label: string; onSelect: () => void };
  };
  /**
   * An unselected slot rather than a real entity — e.g. the org view's
   * always-present "Select user" row. Rendered quiet, never counted as the
   * current entry, and clicking the row opens its switcher menu instead of
   * navigating (there is nothing to navigate to yet).
   */
  placeholder?: boolean;
}

export interface WorkspaceSidebarProps {
  groups: NavGroup[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Ancestry from global down to the current entity (last entry = current). */
  contextStack: ContextStackEntry[];
}

function ContextStackRow({
  entry,
  index,
  isCurrent,
}: {
  entry: ContextStackEntry;
  index: number;
  isCurrent: boolean;
}) {
  const [switcherAnchor, setSwitcherAnchor] = useState<HTMLElement | null>(null);
  const { switcher, placeholder } = entry;

  return (
    <div
      className={
        isCurrent
          ? 'flex items-stretch border-l-4 border-blue-600 bg-blue-50/60'
          : placeholder
            ? 'flex items-stretch border-l-4 border-transparent text-gray-400'
            : 'flex items-stretch border-l-4 border-transparent text-gray-500'
      }
      style={{ paddingLeft: `${index * 14}px` }}
    >
      <button
        type="button"
        onClick={
          placeholder && switcher
            ? event => setSwitcherAnchor(event.currentTarget)
            : entry.onSelect
        }
        title={entry.label}
        className={
          isCurrent
            ? 'min-w-0 flex-1 flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 text-left cursor-pointer hover:bg-blue-100/60'
            : 'min-w-0 flex-1 flex items-center gap-1.5 pl-3 pr-1.5 py-1 text-left cursor-pointer hover:bg-gray-50 hover:text-gray-800'
        }
      >
        {index > 0 && (
          <SubdirectoryArrowRight
            sx={{ fontSize: 14 }}
            className={isCurrent ? 'text-blue-600 shrink-0' : 'text-gray-400 shrink-0'}
          />
        )}
        <span
          className={
            isCurrent
              ? 'min-w-0 truncate text-base font-semibold text-gray-900'
              : placeholder
                ? 'min-w-0 truncate text-sm italic'
                : 'min-w-0 truncate text-sm'
          }
        >
          {entry.label}
        </span>
        {entry.chip && (
          <Chip
            label={entry.chip}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 18, fontSize: 11, flexShrink: 0 }}
          />
        )}
      </button>
      {switcher && (
        <>
          <button
            type="button"
            aria-label={`Switch ${entry.chip?.toLowerCase() ?? 'entity'}`}
            onClick={event => setSwitcherAnchor(event.currentTarget)}
            className={
              isCurrent
                ? 'flex items-center px-1.5 cursor-pointer text-blue-600 hover:bg-blue-100/60'
                : 'flex items-center px-1.5 cursor-pointer text-gray-400 hover:bg-gray-50 hover:text-gray-800'
            }
          >
            <KeyboardArrowDown sx={{ fontSize: 18 }} />
          </button>
          <Menu
            anchorEl={switcherAnchor}
            open={Boolean(switcherAnchor)}
            onClose={() => setSwitcherAnchor(null)}
            slotProps={{ paper: { sx: { maxHeight: 340, minWidth: 232, maxWidth: 320 } } }}
          >
            {switcher.options.map(option => (
              <MenuItem
                key={option.id}
                dense
                selected={option.id === switcher.activeId}
                onClick={() => {
                  setSwitcherAnchor(null);
                  if (option.id !== switcher.activeId) {
                    switcher.onSwitch(option.id);
                  }
                }}
              >
                <ListItemText
                  primary={option.label}
                  secondary={option.secondary}
                  slotProps={{
                    primary: { noWrap: true, sx: { fontSize: '0.875rem' } },
                    secondary: { noWrap: true, sx: { fontSize: '0.75rem' } },
                  }}
                />
              </MenuItem>
            ))}
            {switcher.footer && [
              <Divider key="footer-divider" />,
              <MenuItem
                key="footer"
                dense
                onClick={() => {
                  setSwitcherAnchor(null);
                  switcher.footer?.onSelect();
                }}
              >
                <ListItemText
                  primary={switcher.footer.label}
                  slotProps={{
                    primary: {
                      noWrap: true,
                      sx: { fontSize: '0.875rem', color: 'primary.main' },
                    },
                  }}
                />
              </MenuItem>,
            ]}
          </Menu>
        </>
      )}
    </div>
  );
}

export function WorkspaceSidebar({
  groups,
  activeKey,
  onSelect,
  contextStack,
}: WorkspaceSidebarProps) {
  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full shrink-0">
      {/* Context stack — replaces AdminSidebar's brand band. Ancestors are
          quiet, clickable rows; the current entity is bold with its level
          chip and a primary accent bar (mirrors the selected-nav styling). */}
      {/* The current entity is the deepest real entry — a trailing
          placeholder slot (org view's "Select user") never claims it. */}
      <div className="border-b border-gray-200 py-2">
        {contextStack.map((entry, index) => (
          <ContextStackRow
            key={`${entry.label}-${index}`}
            entry={entry}
            index={index}
            isCurrent={index === contextStack.findLastIndex(e => !e.placeholder)}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <List component="nav" disablePadding>
          {groups.map(group => (
            <Fragment key={group.label}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
              {group.items.map(item => {
                const active = item.key === activeKey;
                return (
                  <ListItemButton
                    key={item.key}
                    onClick={() => onSelect(item.key)}
                    selected={active}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.50',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          color: active ? 'primary.main' : 'text.primary',
                          sx: {
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                          },
                        },
                      }}
                    />
                    {item.badge && (
                      <Chip
                        label={item.badge.label}
                        size="small"
                        color={
                          item.badge.tone === 'warn'
                            ? 'warning'
                            : item.badge.tone === 'success'
                              ? 'success'
                              : 'default'
                        }
                        variant={item.badge.tone === 'success' ? 'outlined' : 'filled'}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                    {item.external && (
                      <OpenInNew sx={{ fontSize: 13, color: 'text.disabled' }} />
                    )}
                  </ListItemButton>
                );
              })}
            </Fragment>
          ))}
        </List>
      </nav>
    </aside>
  );
}
